import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { default as Layout } from '../../components/Layout';
import { 
  Users, 
  BookOpen, 
  Award, 
  MessageSquare, 
  DollarSign, 
  List,
  School,
  AlertCircle,
  FileText,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  Archive,
  PieChart
} from 'lucide-react';
import { api } from '../../api';
import { useAuth } from '../../contexts/AuthContext';

interface UserSummary {
  _id: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
}

interface CourseSummary {
  _id: string;
  title: string;
  professor: { 
    _id: string;
    name: string;
  };
  status: string;
  enrolledStudents: number;
}

interface PaymentSummary {
  _id: string;
  student: {
    _id: string;
    name: string;
    email: string;
  };
  class: {
    name: string;
  } | null;
  amount: string;
  status: string;
  paidAt: string;
}

interface MonthlyStat {
  _id: {
    year: number;
    month: number;
  };
  revenue: string;
  count: number;
}

interface DashboardStats {
  totalUsers: number;
  totalCourses: number;
  totalRevenue: number;
  totalQuiz: number;
  pendingActions: {
    pendingCourses: number;
    pendingPayments: number;
    unreadMessages: number;
    total: number;
  };
  newUsersThisMonth: number;
  coursesThisMonth: number;
  revenueThisMonth: number;
}

interface DashboardResponse {
  success: boolean;
  stats: DashboardStats;
  recentUsers: UserSummary[];
  recentCourses: CourseSummary[];
  recentPayments?: PaymentSummary[];
  monthlyStats?: MonthlyStat[];
  userStats?: {
    total: number;
    students: number;
    professors: number;
    admins: number;
    active: number;
    inactive: number;
    newThisWeek: number;
    newThisMonth: number;
  };
  courseStats?: {
    total: number;
    published: number;
    draft: number;
    pending: number;
    archived: number;
    newThisMonth: number;
  };
  quizStats?: {
    total: number;
    active: number;
    totalAttempts: number;
    averageScore: number;
  };
  paymentStats?: {
    total: number;
    succeeded: number;
    pending: number;
    failed: number;
    totalRevenue: number;
    revenueThisMonth: number;
  };
}

const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await api.get('/admin/dashboard');
        setDashboardData(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchDashboardData();
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-brand-50 border border-brand-200 text-brand-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </Layout>
    );
  }

  if (!dashboardData) {
    return (
      <Layout>
        <div className="text-center py-8 text-gray-500">
          <AlertCircle className="h-12 w-12 mx-auto mb-3" />
          <p>No dashboard data available.</p>
        </div>
      </Layout>
    );
  }

  const { 
    stats, 
    recentUsers, 
    recentCourses, 
    recentPayments = [], 
    monthlyStats = [], 
    userStats, 
    courseStats, 
    quizStats, 
    paymentStats
  } = dashboardData;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'published':
      case 'succeeded':
      case 'active':
        return 'bg-brand-100 text-brand-800';
      case 'pending':
        return 'bg-brand-100 text-brand-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'failed':
      case 'archived':
        return 'bg-brand-100 text-brand-800';
      default:
        return 'bg-brand-100 text-brand-800';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Tableau de bord Administrateur</h1>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Link to="/admin/users" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow">
            <Users className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">Utilisateurs</span>
          </Link>
          <Link to="/admin/category" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow">
            <List className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">Catégories</span>
          </Link>
          <Link to="/admin/courses" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow">
            <BookOpen className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">Cours</span>
          </Link>
          <Link to="/admin/classes" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow">
            <School className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">Classes</span>
          </Link>
          <Link to="/admin/quizzes" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow">
            <Award className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">Quiz</span>
          </Link>
          <Link to="/admin/payments" className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center hover:shadow-md transition-shadow">
            <DollarSign className="h-12 w-12 text-brand-600 mb-3" />
            <span className="text-lg font-semibold text-gray-800">Paiements</span>
          </Link>
        </div>

        {/* Main Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <Users className="h-10 w-10 text-brand-500" />
              <div>
                <p className="text-gray-500">Total Utilisateurs</p>
                <h2 className="text-2xl font-bold text-gray-900">{stats.totalUsers}</h2>
                {userStats && (
                  <p className="text-sm text-gray-600">
                    +{userStats.newThisMonth} ce mois
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <BookOpen className="h-10 w-10 text-brand-500" />
              <div>
                <p className="text-gray-500">Total Cours</p>
                <h2 className="text-2xl font-bold text-gray-900">{stats.totalCourses}</h2>
                {courseStats && (
                  <p className="text-sm text-gray-600">
                    +{courseStats.newThisMonth} ce mois
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <DollarSign className="h-10 w-10 text-brand-500" />
              <div>
                <p className="text-gray-500">Revenu Total</p>
                <h2 className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</h2>
                {paymentStats && (
                  <p className="text-sm text-gray-600">
                    {formatCurrency(paymentStats.revenueThisMonth)} ce mois
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4">
              <MessageSquare className="h-10 w-10 text-brand-500" />
              <div>
                <p className="text-gray-500">Actions en attente</p>
                <h2 className="text-2xl font-bold text-gray-900">{stats.pendingActions.total}</h2>
                <div className="flex space-x-2 text-xs">
                  <span className="text-gray-600">{stats.pendingActions.pendingCourses} cours</span>
                  <span className="text-gray-600">{stats.pendingActions.pendingPayments} paiements</span>
                  <span className="text-gray-600">{stats.pendingActions.unreadMessages} messages</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Stats Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Users Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2" /> Statistiques Utilisateurs
            </h2>
            {userStats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <p className="text-sm text-brand-800">Étudiants</p>
                    <p className="text-xl font-bold">{userStats.students}</p>
                  </div>
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <p className="text-sm text-brand-800">Professeurs</p>
                    <p className="text-xl font-bold">{userStats.professors}</p>
                  </div>
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <p className="text-sm text-brand-800">Actifs</p>
                    <p className="text-xl font-bold">{userStats.active}</p>
                  </div>
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <p className="text-sm text-brand-800">Inactifs</p>
                    <p className="text-xl font-bold">{userStats.inactive}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{userStats.newThisWeek}</span> nouveaux cette semaine
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{userStats.newThisMonth}</span> nouveaux ce mois
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-3" />
                <p>Aucune donnée utilisateur disponible.</p>
              </div>
            )}
          </div>

          {/* Courses Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <BookOpen className="h-5 w-5 mr-2" /> Statistiques Cours
            </h2>
            {courseStats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <p className="text-sm text-brand-800">Publiés</p>
                    <p className="text-xl font-bold">{courseStats.published}</p>
                  </div>
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <p className="text-sm text-brand-800">En attente</p>
                    <p className="text-xl font-bold">{courseStats.pending}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-800">Brouillons</p>
                    <p className="text-xl font-bold">{courseStats.draft}</p>
                  </div>
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <p className="text-sm text-brand-800">Archivés</p>
                    <p className="text-xl font-bold">{courseStats.archived}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{courseStats.newThisMonth}</span> nouveaux ce mois
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-3" />
                <p>Aucune donnée cours disponible.</p>
              </div>
            )}
          </div>

          {/* Payment Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" /> Statistiques Paiements
            </h2>
            {paymentStats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <p className="text-sm text-brand-800">Payés</p>
                    <p className="text-xl font-bold">{paymentStats.succeeded}</p>
                  </div>
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <p className="text-sm text-brand-800">Impayés</p>
                    <p className="text-xl font-bold">{paymentStats.pending}</p>
                  </div>
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <p className="text-sm text-brand-800">Annulés</p>
                    <p className="text-xl font-bold">{paymentStats.failed}</p>
                  </div>
                  <div className="bg-brand-50 p-3 rounded-lg">
                    <p className="text-sm text-brand-800">Total</p>
                    <p className="text-xl font-bold">{paymentStats.total}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    Revenu mensuel: <span className="font-medium">{formatCurrency(paymentStats.revenueThisMonth)}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Revenu total: <span className="font-medium">{formatCurrency(paymentStats.totalRevenue)}</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-3" />
                <p>Aucune donnée paiement disponible.</p>
              </div>
            )}
          </div>
        </div>

        {/* Additional Stats Sections */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Award className="h-5 w-5 mr-2" /> Statistiques Quiz
          </h2>
          {quizStats ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-brand-50 p-3 rounded-lg">
                  <p className="text-sm text-brand-800">Total Quiz</p>
                  <p className="text-xl font-bold">{quizStats.total}</p>
                </div>
                <div className="bg-brand-50 p-3 rounded-lg">
                  <p className="text-sm text-brand-800">Actifs</p>
                  <p className="text-xl font-bold">{quizStats.active}</p>
                </div>
                <div className="bg-brand-50 p-3 rounded-lg">
                  <p className="text-sm text-brand-800">Tentatives</p>
                  <p className="text-xl font-bold">{quizStats.totalAttempts}</p>
                </div>
                <div className="bg-brand-50 p-3 rounded-lg">
                  <p className="text-sm text-brand-800">Moyenne Score</p>
                  <p className="text-xl font-bold">{quizStats.averageScore.toFixed(1)}%</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-3" />
              <p>Aucune donnée quiz disponible.</p>
            </div>
          )}
        </div>

        {/* Recent Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Users */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2" /> Nouveaux Utilisateurs
            </h2>
            {recentUsers.length > 0 ? (
              <div className="space-y-3">
                {recentUsers.map(user => (
                  <div key={user._id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 bg-brand-100 p-2 rounded-full">
                      <Users className="h-5 w-5 text-brand-600" />
                    </div>
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      <div className="flex items-center mt-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(user.role)}`}>
                          {user.role}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          {formatDate(user.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-3" />
                <p>Aucun nouvel utilisateur.</p>
              </div>
            )}
          </div>

          {/* Recent Courses */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <BookOpen className="h-5 w-5 mr-2" /> Derniers Cours
            </h2>
            {recentCourses.length > 0 ? (
              <div className="space-y-3">
                {recentCourses.map(course => (
                  <div key={course._id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 bg-brand-100 p-2 rounded-full">
                      <BookOpen className="h-5 w-5 text-brand-600" />
                    </div>
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">{course.title}</p>
                      <p className="text-sm text-gray-600">Par {course.professor.name}</p>
                      <div className="flex items-center mt-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(course.status)}`}>
                          {course.status}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          {course.enrolledStudents} étudiants
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-3" />
                <p>Aucun cours récent.</p>
              </div>
            )}
          </div>

          {/* Recent Payments */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" /> Derniers Paiements
            </h2>
            {recentPayments.length > 0 ? (
              <div className="space-y-3">
                {recentPayments.map(payment => (
                  <div key={payment._id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 bg-brand-100 p-2 rounded-full">
                      <DollarSign className="h-5 w-5 text-brand-600" />
                    </div>
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">{payment.student.name}</p>
                      <p className="text-sm text-gray-600">{payment.class?.name || '—'}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(payment.status)}`}>
                          {payment.status}
                        </span>
                        <span className="text-xs font-medium">
                          {formatCurrency(parseFloat(payment.amount))}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatDate(payment.paidAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-3" />
                <p>Aucun paiement récent.</p>
              </div>
            )}
          </div>
        </div>

        {/* Monthly Revenue Chart */}
        {monthlyStats.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Activity className="h-5 w-5 mr-2" /> Revenus Mensuels
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {monthlyStats.map((stat, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className="w-full bg-brand-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-brand-800">
                      {new Date(stat._id.year, stat._id.month - 1).toLocaleDateString('fr-FR', { month: 'short' })}
                    </p>
                    <p className="text-xl font-bold text-brand-600">
                      {formatCurrency(parseFloat(stat.revenue))}
                    </p>
                    <p className="text-xs text-gray-600">
                      {stat.count} transactions
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminDashboard;